import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AppShell } from './AppShell';
import { AccountProvider } from './accounts';
import { FolderProvider } from './folders';
import { PreferencesProvider } from './preferences';
import { ErrorBoundary } from './ErrorBoundary';
import { SessionProvider } from './session';
import { RequireSession } from './guards';
import { UnsavedChangesProvider } from './unsavedChanges';
import { IdlePrefetch } from './prefetch';
import {
  AccountsSettings,
  AdminView,
  AiSettings,
  CalendarView,
  ComposeWindowView,
  ContactsView,
  FiltersSettings,
  GeneralSettings,
  MessageWindowView,
  NextcloudSettings,
  NotificationsSettings,
  PrivacySettings,
  ScheduledView,
  SessionsSettings,
  SettingsIndex,
  SettingsView,
  SnoozedView,
  TasksView,
  VacationSettings,
} from './lazyRoutes';
import { MailView } from '@/routes/mail/MailView';
import { OUTBOX_SEGMENT } from '@/features/outbox/route';
import { OutboxView } from '@/features/outbox/OutboxView';
import { SCHEDULED_SEGMENT, SNOOZED_SEGMENT } from '@/features/scheduling/route';
import { LABEL_SEGMENT, UNIFIED_SEGMENT } from '@/routes/mail/unifiedRoute';
import { LoginView } from '@/routes/LoginView';
import { NotFoundView } from '@/routes/NotFoundView';
import { MailtoRedirect } from '@/routes/mail/MailtoRedirect';

// Every view but mail is a lazy chunk with its own skeleton: lazyRoutes.tsx.

/**
 * Session state sits above *everything routed*, `/login` included.
 *
 * `/login` needs it too: it has to know whether the visitor is already signed
 * in (so a bookmarked `/login` does not ask a signed-in user for a password),
 * and signing in has to publish the new session to the same provider the shell
 * reads — a second provider under `/login` would mean the shell never hears
 * about the login that just happened.
 */
function SessionRoot() {
  return (
    <SessionProvider>
      <Outlet />
    </SessionProvider>
  );
}

/**
 * Account + folder state is loaded once, above every routed view, and so are
 * the owner's preferences: the date format has to be in place before the first
 * timestamp renders.
 */
function ShellRoot() {
  return (
    <RequireSession>
      <PreferencesProvider>
        <AccountProvider>
          <FolderProvider>
            {/* One router blocker for every settings/admin card with a Save (#343). */}
            <UnsavedChangesProvider>
              <AppShell />
            </UnsavedChangesProvider>
            {/* Warms likely-next chunks once the shell has painted (#208). */}
            <IdlePrefetch />
          </FolderProvider>
        </AccountProvider>
      </PreferencesProvider>
    </RequireSession>
  );
}

/** The shell's routed children, shared by the app router and by tests. */
export const shellChildRoutes = [
    { index: true, element: <Navigate to="/mail" replace /> },

    // Mail: folder and open message both live in the URL.
    { path: 'mail', element: <MailView /> },
    /*
     * The unified view (ADR 0056), before the folder routes it must outrank.
     * `unified` is a static segment, so react-router ranks it above
     * `mail/:folderId` whatever the order here; the open message carries its
     * account because a message id is unique only within one (ADR 0012).
     */
    /*
     * The Outbox (issue #201). Static, like `unified`, so it outranks
     * `mail/:folderId`; a route of its own rather than a mode of `MailView`,
     * because nothing in it comes from the server and it shares no state with
     * the message list. Not lazy, deliberately: this is the one route whose
     * whole reason for existing is that there is no network to fetch a chunk
     * over, and the precache is a weaker promise than a static import.
     */
    { path: `mail/${OUTBOX_SEGMENT}`, element: <OutboxView /> },
    /*
     * Send later and Snooze (ADR 0076 §17): the jobs behind the `Scheduled`
     * and `Snoozed` folders. Static like the Outbox, so they outrank
     * `mail/:folderId`; the folder rows with those roles link here.
     */
    { path: `mail/${SCHEDULED_SEGMENT}`, element: <ScheduledView /> },
    { path: `mail/${SNOOZED_SEGMENT}`, element: <SnoozedView /> },
    { path: `mail/${UNIFIED_SEGMENT}`, element: <MailView unified /> },
    { path: `mail/${UNIFIED_SEGMENT}/:accountId/:messageId`, element: <MailView unified /> },
    { path: `mail/${LABEL_SEGMENT}/:labelAccountId/:labelId`, element: <MailView /> },
    { path: `mail/${LABEL_SEGMENT}/:labelAccountId/:labelId/:messageId`, element: <MailView /> },
    { path: 'mail/:folderId', element: <MailView /> },
    { path: 'mail/:folderId/:messageId', element: <MailView /> },

    // Calendar: the view mode is a route segment so a week view is linkable.
    { path: 'calendar', element: <Navigate to="/calendar/month" replace /> },
    { path: 'calendar/:view', element: <CalendarView /> },

    { path: 'contacts', element: <ContactsView /> },
    { path: 'contacts/:contactId', element: <ContactsView /> },

    // Tasks (ADR 0069): the list (or smart view) and the open task are both
    // in the URL, so a phone's Back closes a task and a list is linkable.
    { path: 'tasks', element: <TasksView /> },
    { path: 'tasks/:listId', element: <TasksView /> },
    { path: 'tasks/:listId/:taskId', element: <TasksView /> },

    {
      path: 'settings',
      element: <SettingsView />,
      children: [
        { index: true, element: <SettingsIndex /> },
        { path: 'general', element: <GeneralSettings /> },
        { path: 'accounts', element: <AccountsSettings /> },
        { path: 'filters', element: <FiltersSettings /> },
        { path: 'out-of-office', element: <VacationSettings /> },
        { path: 'notifications', element: <NotificationsSettings /> },
        { path: 'privacy', element: <PrivacySettings /> },
        { path: 'ai', element: <AiSettings /> },
        { path: 'nextcloud', element: <NextcloudSettings /> },
        { path: 'sessions', element: <SessionsSettings /> },
      ],
    },

    // The view itself renders NotFound for a non-admin; the server's 404 on
    // every /admin/* route is the actual control.
    { path: 'admin', element: <AdminView /> },
    { path: 'admin/:tab', element: <AdminView /> },

    { path: '*', element: <NotFoundView /> },
];

export const router = createBrowserRouter([
  {
    element: <SessionRoot />,
    /*
     * The boundary for everything outside the shell -- `/login`, the pop-out
     * message and compose windows, `/mailto` -- so they show the Reload screen
     * (and report the error, ADR 0067) instead of react-router's default page.
     */
    errorElement: <ErrorBoundary />,
    children: [
      { path: '/login', element: <LoginView /> },
      /*
       * A real route for a real window (ADR 0020). Under `SessionRoot` so the
       * login redirect and the re-auth dialog work inside the popup, and
       * deliberately NOT under `ShellRoot`: no sidebar, list or search.
       */
      {
        path: '/message/:accountId/:messageId',
        element: (
          <RequireSession>
            <PreferencesProvider>{<MessageWindowView />}</PreferencesProvider>
          </RequireSession>
        ),
      },
      /*
       * The composer popped out of the mail view. Same terms as the message
       * window: a real route under `SessionRoot`, not a modal in a popup, so a
       * blocked or bookmarked window still lands somewhere that works.
       */
      {
        path: '/compose/:accountId',
        element: (
          <RequireSession>
            <PreferencesProvider>{<ComposeWindowView />}</PreferencesProvider>
          </RequireSession>
        ),
      },
      /*
       * The `mailto:` handler the manifest registers. It only redirects, so it
       * is eager rather than lazy: a person clicking a mailto link is already
       * waiting on a cold start, and this adds no bytes worth splitting.
       */
      {
        path: '/mailto',
        element: (
          <RequireSession>
            <MailtoRedirect />
          </RequireSession>
        ),
      },
      {
        path: '/',
        element: <ShellRoot />,
        errorElement: <ErrorBoundary />,
        children: shellChildRoutes,
      },
    ],
  },
]);

/** Exported for tests / storybook-style harnesses. */
export { Outlet };
